import React, { Fragment, memo, useEffect, useRef, useMemo, useCallback } from "react";

import AxiomLoader from "__AxiomComponents/AxiomUI/AxiomLoader/AxiomLoader.jsx";
import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";

import HawkeyeV2Widget from "../HawkeyeV2Widget/HawkeyeV2Widget";
import HawkeyeNavbar from "../HawkeyeNavbar/HawkeyeNavbar";
import HawkeyeGlobalFilters from "../HawkeyeGlobalFilters/HawkeyeGlobalFilters";
import HawkeyeTopBar from "../HawkeyeTopBar/HawkeyeTopBar";
import RemToPx from "__AxiomComponents/RemToPx/RemToPx";
import { useOnPageSelection, useSetGlobalFilters } from "./HawkeyeV2Hooks.js";
import "./HawkeyeV2.scss";
import { checkMobileDevice } from "../../../Home/components/homeUtils.js";
import { useSetCurrentPage } from "../HawkeyeSearch/HawkeyeSearchHooks.js";

function HawkeyeV2(props) {
  const { appSettings, userInfo, logout, isLogoutDisabled } = props;

  const [state, dispatch] = useAxiomState(
    {
      isDataFetched: false,
      loading: true,
      dashboard: "",
      pages: [],
      activePageIndex: [],
      KPIWiseConfigs: null,
      globalFilters: null,
      selectedGlobalFilter: null,
      shouldRedirectToHome: false,
      collapse: true,
      showMobileViewAnyways: false,
    },
    props.appName,
    props.appID,
    "HawkeyeV2",
    props.componentID
  );
  const hawkeyeAppRef = useRef(null);
  const hawkeyeContentRef = useRef(null);

  // Adding finalDashoardSettings which is combination of appSettings, dashboardSettings, and userDashboardSettings
  const finalDashboardSettings = useMemo(() => {
    const mergedSettings = {
      ...appSettings,
      ...state.dashboardSettings,
      ...state.userDashboardSettings,
    };
    return mergedSettings;
  }, [state.dashboardSettings, state.userDashboardSettings, appSettings]);

  // On initial load if dashboard URL parameter is available fetch pages for the dashboard
  // else redirect to home
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("dashboard")) {
      // We replace ~ with space because we have replaced space with ~ while generating URL
      // from dashboard name
      const dashboard = urlParams.get("dashboard").replace(/~/g, " ");
      // check for device type (mobile or desktop)
      const isMobileDevice = checkMobileDevice();

      dispatch({ dashboard: dashboard, isMobileDevice: isMobileDevice }, "AxiomEvent", "getPages");
      dispatch({ dashboard: dashboard }, "AxiomEvent", "getDashboardSettings");
    } else {
      location.href = "/apps/Home/";
    }
  }, [dispatch]);

  useEffect(() => {
    if (state.activePageIndex.length == 0 && state.pages.length > 0) {
      let pages = [...state.pages];
      const activePageIndex = [0];

      while (pages[0].subpages.length > 0) {
        activePageIndex.push(0);
        pages = pages[0].subpages;
      }
      dispatch({ activePageIndex: activePageIndex });
    }
  }, [state.pages, state.activePageIndex, dispatch]);

  const currentPage = useSetCurrentPage(state.pages, state.activePageIndex);

  // On current page change fetch KPIWiseConfigs for all the KPIs in this page
  useEffect(() => {
    if (currentPage) {
      dispatch(
        {
          KPIWiseConfigs: null,
        },
        "AxiomEventInfo"
      );
      dispatch(
        {
          currentPage: currentPage,
        },
        "AxiomEvent",
        "getPageDetails"
      );
    }
  }, [dispatch, currentPage]);

  // If there is an error while fetching widget data redirect user to home
  useEffect(() => {
    if (state.shouldRedirectToHome) {
      location.href = "/apps/Home/";
    }
  }, [state.shouldRedirectToHome]);

  const onClickExpandCollapseNavBar = useCallback(
    (finalDashboardSettings, collapse) => {
      if (finalDashboardSettings["is-left-nav-collapsed"]) {
        if (collapse && hawkeyeAppRef.current != null) {
          d3.select(".hawkeye-navbar").classed("HamOpen", true);
        } else {
          d3.select(".hawkeye-navbar").classed("HamOpen", false);
        }
      }
      dispatch({ collapse: !collapse });
    },
    [dispatch]
  );

  const expandCollapseNavBar = (finalDashboardSettings) => {
    if (finalDashboardSettings) {
      const autoCollapsedPoint = finalDashboardSettings["auto-collapsed-point"] || 1300;
      const autoTwoColumnPoint = finalDashboardSettings["two-column-break-point"] || 1300;
      if (
        finalDashboardSettings["is-left-nav-collapsed"] &&
        finalDashboardSettings["is-right-nav-collapsed"] &&
        hawkeyeAppRef.current != null
      ) {
        //CBT:if both nav bar collapsed
        if (
          finalDashboardSettings["is-left-nav-collapsed"] &&
          finalDashboardSettings["is-right-nav-collapsed"]
        )
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app both-nav-collapsed", true);
      } else if (finalDashboardSettings["is-right-nav-collapsed"]) {
        //CBT:if only right nav bar collapsed
        if (window.innerWidth < autoCollapsedPoint && hawkeyeAppRef.current != null) {
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app both-nav-collapsed", true);
        } else {
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app both-nav-collapsed", false);
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app right-nav-collapsed", true);
        }
      } else if (finalDashboardSettings["is-left-nav-collapsed"]) {
        //CBT:if only left nav bar collapsed
        if (window.innerWidth < autoCollapsedPoint && hawkeyeAppRef.current != null) {
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app both-nav-collapsed", true);
        } else {
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app both-nav-collapsed", false);
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app left-nav-collapsed", true);
        }
      } else {
        //CBT:if both nav bar expanded
        if (window.innerWidth < autoCollapsedPoint && hawkeyeAppRef.current != null) {
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app both-nav-collapsed", true);
        } else {
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app both-nav-collapsed", false);
          d3.select(hawkeyeAppRef.current).classed("hawkeye-app", true);
        }
      }
      if (
        hawkeyeContentRef &&
        hawkeyeContentRef.current != null &&
        finalDashboardSettings["is-two-column-view"] &&
        window.innerWidth > autoTwoColumnPoint
      ) {
        d3.select(hawkeyeContentRef.current).classed(
          "hawkeye-content hawkeye-content-two-columns",
          true
        );
        document.documentElement.classList.add("is-expanded");
      } else {
        d3.select(hawkeyeContentRef.current).classed(
          "hawkeye-content hawkeye-content-two-columns",
          false
        );
        d3.select(hawkeyeContentRef.current).classed("hawkeye-content", true);
        document.documentElement.classList.remove("is-expanded");
      }
    }
  };

  const showMobileViewAnyways = () => {
    try {
      document.body.requestFullscreen().catch(console.log);
      screen.orientation.lock("landscape").catch(console.log);
    } catch (error) {
      console.error(error);
    }
    dispatch({ showMobileViewAnyways: true });
  };

  useEffect(() => {
    expandCollapseNavBar(finalDashboardSettings);
    const callExpandCollapseNavBar = () => {
      expandCollapseNavBar(finalDashboardSettings);
    };
    window.addEventListener("resize", callExpandCollapseNavBar);
    return () => {
      window.removeEventListener("resize", callExpandCollapseNavBar);
    };
  }, [finalDashboardSettings]);

  // On page & widget selection event listener
  const onPageSelection = useOnPageSelection(dispatch, RemToPx);

  const setGlobalFilters = useSetGlobalFilters(dispatch);

  const logo =
    finalDashboardSettings && finalDashboardSettings.appLogo
      ? finalDashboardSettings.appLogo
      : "images/hawkeye-logo.svg";

  const isMobileDevice = checkMobileDevice(finalDashboardSettings.forcefullyDisableMobileView);

  if (state.isDataFetched) {
    return (
      <Fragment>
        <div className="hawkeye-story-portal" />
        <div className="hawkeye-filter-portal" />

        {(!isMobileDevice || state.showMobileViewAnyways) && (
          <Fragment>
            <HawkeyeTopBar
              dashboard={state.dashboard}
              pages={state.pages}
              currentPageIndex={state.activePageIndex}
              onSelection={onPageSelection}
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID}
              userInfo={userInfo}
              logout={logout}
              isLogoutDisabled={isLogoutDisabled}
              logo={logo}
              appSettings={finalDashboardSettings}
              collapse={state.collapse}
              onClickExpandCollapseNavBar={onClickExpandCollapseNavBar}
            />
            <div className="hawkeye-app" ref={hawkeyeAppRef}>
              <HawkeyeNavbar
                versionFileContent={props.versionFileContent}
                expandCollapseNavBar={expandCollapseNavBar}
                appSettings={finalDashboardSettings}
              />
              <div className="hawkeye-content" ref={hawkeyeContentRef}>
                {currentPage &&
                  currentPage.widgets.map((widget, index) => (
                    <HawkeyeV2Widget
                      key={widget.widgetID}
                      widget={widget}
                      KPIWiseConfigs={state.KPIWiseConfigs}
                      appName={props.appName}
                      appID={props.appID}
                      componentID={widget.widgetID}
                      selectedGlobalFilter={state.selectedGlobalFilter}
                      currentPage={currentPage}
                      setGlobalFilters={setGlobalFilters}
                      HawkeyeV2AppSettings={finalDashboardSettings}
                    />
                  ))}
              </div>
              <div className="hawkeye-right-bar">
                {state.globalFilters != null && (
                  <HawkeyeGlobalFilters
                    appName={props.appName}
                    appID={props.appID}
                    componentID={`globalFilterID`}
                    globalFilters={state.globalFilters}
                    setGlobalFilters={setGlobalFilters}
                    selectedGlobalFilter={state.selectedGlobalFilter}
                  />
                )}
              </div>
            </div>
          </Fragment>
        )}

        {isMobileDevice && !state.showMobileViewAnyways && (
          <div className="hawkeye-mobile-message">
            <p className="hawkeye-mobile-text">
              This app is not optimized for small screen devices. Go to homepage to see all
              compatible apps. If you still want to access the view, it's advisable to access it on
              the landscape mode.
            </p>
            <div>
              <a className="hawkeye-btn hawkeye-btn-primary" href="/apps/Home/#/all-apps">
                Show me all compatible apps
              </a>
              <a onClick={showMobileViewAnyways} className="hawkeye-btn hawkeye-btn-secondary">
                Show me this view anyways
              </a>
            </div>
          </div>
        )}

        <div className="hawkeye-portrait-message">
          Please enable auto rotate and change to Landscape mode for better experience.
        </div>

        {finalDashboardSettings &&
        finalDashboardSettings.appCopyRight &&
        finalDashboardSettings.appCopyRight.copyrightText &&
        finalDashboardSettings.appCopyRight.isCopyRightText.toString() == "true" ? (
          <div className="copyright-hawkeye-bottom-container">
            {" "}
            {finalDashboardSettings.appCopyRight.copyrightText}
          </div>
        ) : null}
      </Fragment>
    );
  } else {
    return (
      <AxiomLoader background={"rgba(255, 255, 255, 0.4)"} fullScreen={true} block=".axiom-app" />
    );
  }
}

export default memo(HawkeyeV2);
